'use client'

import React, { FC, ReactElement } from 'react'
import styles from './Details.module.css'
import classNames from 'classnames'
import { MEMPOOL_DOT_SPACE, CDN_URL } from '@/utils/constants'
import Outpoint from '@/utils/outpoint'
import Link from '@/components/link'
import { InscriptionDetail } from '@/utils/types/inscription'

import useInscriptionOutpoint from '@/utils/hooks/useInscriptionOutpoint'
import InscriptionAttribute from '../inscription-attribute'
import { MonkePageHistory } from '@/components/nodemonkes/inscription-panels'

const Details: FC<{ inscription: InscriptionDetail }> = (props): ReactElement => {
	const { inscription } = props
	const { data: outpointData } = useInscriptionOutpoint(inscription?.id)

	const isMonke = inscription?.collection?.slug === 'nodemonkes'
	const hasAttributes = !isMonke && !!inscription?.meta?.attributes
	const propertiesAttributes = React.useMemo(() => {
		const traits = inscription?.properties?.attributes?.traits
		if (!traits || Object.keys(traits).length === 0) return []
		return Object.entries(traits).map(([k, v]) => ({
			trait_type: k,
			value: String(v)
		}))
	}, [inscription?.properties?.attributes?.traits])

	const renderLocation = React.useCallback(() => {
		const { txid, vout } = Outpoint.decode(outpointData?.inscription?.outpoint)

		return (
			<a
				className={classNames(styles.linkValue, 'text-sm', 'semibold')}
				target="blank"
				href={`${MEMPOOL_DOT_SPACE}/tx/${txid}`}
			>
				{txid}:{vout}
			</a>
		)
	}, [outpointData])

	return (
		<>
			{isMonke && <MonkePageHistory id={inscription.id} />}
			{hasAttributes && (
				<div className={styles.root}>
					{/* <p className={styles.title}>Attributes</p> */}
					<div className={styles.attributes}>
						{inscription?.meta?.attributes?.map((e, i) => (
							<InscriptionAttribute
								key={i}
								index={i}
								attribute={e}
								total={inscription?.meta?.attributes?.length}
							/>
						))}
					</div>
				</div>
			)}
			{propertiesAttributes.length > 0 && (
				<div className={styles.root}>
					<div className={styles.attributes}>
						{propertiesAttributes.map((attr, i) => (
							<InscriptionAttribute
								key={i}
								index={i}
								attribute={attr}
								total={propertiesAttributes.length}
							/>
						))}
					</div>
				</div>
			)}
			<div className={styles.root}>
				<div className={styles.containerBody}>
					<div className={styles.row}>
						<p className={classNames(styles.key, 'text-xs', 'semibold')}>ID</p>
						<p className={classNames(styles.value, 'text-xs', 'semibold')}>{inscription?.id}</p>
					</div>
					<div className={styles.row}>
						<p className={classNames(styles.key, 'text-xs', 'semibold')}>Inscription Number</p>
						<p className={classNames(styles.value, 'text-xs', 'semibold')}>
							{inscription?.num}
						</p>
					</div>
					<div className={styles.row}>
						<p className={classNames(styles.key, 'text-xs', 'semibold')}>Owner</p>
						{outpointData?.owner ? (
							<Link
								className={classNames(styles.linkValue, 'text-sm', 'semibold')}
								href={`/address/${outpointData?.owner}`}
							>
								{outpointData?.owner}
							</Link>
						) : outpointData ? (
							<p className={classNames(styles.value, 'text-sm', 'semibold')}>—</p>
						) : (
							<span aria-hidden="true" className={classNames(styles.valueSkele, 'inline-block h-4 w-32 rounded bg-muted motion-safe:animate-pulse')} />
						)}
					</div>
					<div className={styles.row}>
						<p className={classNames(styles.key, 'text-xs', 'semibold')}>Content</p>
						<a
							target="_blank"
							className={classNames(styles.linkValue, 'text-sm', 'semibold')}
							href={`${CDN_URL}/${inscription?.id}`}
						>
							link
						</a>
					</div>
					<div className={styles.row}>
						<p className={classNames(styles.key, 'text-xs', 'semibold')}>Content Length</p>
						<p className={classNames(styles.value, 'text-sm', 'semibold')}>
							{inscription?.content_length} Bytes
						</p>
					</div>
					<div className={styles.row}>
						<p className={classNames(styles.key, 'text-xs', 'semibold')}>Content Type</p>
						<p className={classNames(styles.value, 'text-sm', 'semibold')}>
							{inscription?.effective_content_type ?? inscription?.content_type}
						</p>
					</div>
					<div className={styles.row}>
						<p className={classNames(styles.key, 'text-xs', 'semibold')}>Created</p>
						<p className={classNames(styles.value, 'text-sm', 'semibold')}>
							{new Date(inscription?.created * 1000).toLocaleString('en-US', { timeZone: 'UTC' })} UTC
						</p>
					</div>
					<div className={styles.row}>
						<p className={classNames(styles.key, 'text-xs', 'semibold')}>Genesis Height</p>
						<p className={classNames(styles.value, 'text-sm', 'semibold')}>
							{inscription?.genesis_height}
						</p>
					</div>
					<div className={styles.row}>
						<p className={classNames(styles.key, 'text-xs', 'semibold')}>Genesis Fee</p>
						<p className={classNames(styles.value, 'text-sm', 'semibold')}>
							{inscription?.genesis_fee}
						</p>
					</div>
						<div className={styles.row}>
						<p className={classNames(styles.key, 'text-xs', 'semibold')}>Location</p>
						{outpointData?.inscription?.outpoint ? (
							renderLocation()
						) : outpointData ? (
							<p className={classNames(styles.value, 'text-sm', 'semibold')}>—</p>
						) : (
							<span aria-hidden="true" className={classNames(styles.valueSkele, 'inline-block h-4 w-32 rounded bg-muted motion-safe:animate-pulse')} />
						)}
					</div>
				</div>
			</div>
		</>
	)
}

export default Details
